import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { useFormik } from 'formik';
import * as Icon from 'react-feather';
import * as Yup from 'yup';
import FormDateInput from '../../../components/form/FormDateInput';
import FormSelect from '../../../components/form/FormSelect';
import { useDispatch } from 'react-redux';
import {
  archiveOptionsGet,
  processAllOptionGet,
  userOptionGetByProcess
} from '../../../store/slices/tenantSlices/dailyReportSlice';
import { endOfDay, startOfDay } from 'date-fns';
import AdminHeader from '../../../components/header/AdminHeader';
import Pagination from '../../../components/form/Pagination';
import CallexLoader from '../../../components/CallexLoader';
import {
  auditorOptionsGet,
  auditorReportsGet
} from '../../../store/slices/tenantSlices/auditorSlice';
import {
  formatArchiveOptions,
  formatPayloadDate,
  getDateRangeFromLabel
} from '../../../utils/common';
import { useTimezone } from '../../../store/slices/commonSlice';
import { toZonedTime } from 'date-fns-tz';

const TenantAuditReport = () => {
  const dispatch = useDispatch();
  const timezone = useTimezone();

  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [processOptions, setProcessOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [auditorOptions, setAuditorOptions] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [archiveOptions, setArchiveOptions] = useState([]);
  const [paginationDetails, setPaginationDetails] = useState({ page: 1, total: 0, limit: 10 });

  const today = toZonedTime(new Date(), timezone);

  const initialValues = {
    page: 1,
    limit: 10,
    auditorId: '',
    userIds: [],
    processIds: [],
    from_date: startOfDay(today),
    to_date: endOfDay(today),
    archiveId: 0
  };

  const validationSchema = Yup.object({
    from_date: Yup.date().required('Start date is required'),
    to_date: Yup.date().required('End date is required'),
    processIds: Yup.array().min(1, 'Process is required').required('Process is required')
  });

  const onSubmit = async () => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const formattedStartDate = formatPayloadDate(values.from_date);
      const formattedEndDate = formatPayloadDate(values.to_date);
      const payload = { ...values, auditorId: values?.auditorId || undefined };
      payload['from_date'] = formattedStartDate;
      payload['to_date'] = formattedEndDate;
      payload['processIds'] = values?.processIds;
      payload['userIds'] = values?.userIds || undefined;

      const response = await dispatch(auditorReportsGet(payload)).unwrap();
      if (response) {
        setReportData(response || []);
        setPaginationDetails({
          ...paginationDetails,
          limit: response.limit,
          page: response.page,
          total: response.total
        });
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  const { values, handleChange, handleBlur, setFieldValue, handleSubmit, touched, errors } =
    useFormik({ initialValues, validationSchema, onSubmit });

  useEffect(() => {
    if (values?.processIds.length > 0) {
      onSubmit();
    }
  }, [values?.limit, values?.page]);

  const getProcessOption = async () => {
    try {
      const response = await dispatch(processAllOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({ label: val.name, value: val.id }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  const getAuditorOptions = async () => {
    try {
      const response = await dispatch(auditorOptionsGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({ label: val.username, value: val.id }));
        setAuditorOptions(options);
      } else {
        setAuditorOptions([]);
      }
    } catch (error) {
      setAuditorOptions([]);
      console.log('error', error);
    }
  };

  const getArchiveOptions = async () => {
    try {
      const response = await dispatch(archiveOptionsGet()).unwrap();
      if (response && response.data) {
        const options = formatArchiveOptions(response.data, timezone);
        setArchiveOptions(options);
      } else {
        setArchiveOptions([]);
      }
    } catch (error) {
      setArchiveOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getArchiveOptions();
    getProcessOption();
    getAuditorOptions();
  }, []);

  const getUserOption = async () => {
    try {
      const serializedProcessId = `[${[values?.processIds].join(',')}]`;
      const response = await dispatch(
        userOptionGetByProcess({ processIds: serializedProcessId })
      ).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({ label: val.Username || '-', value: val.id }));
        setUserOptions(options);
      } else {
        setUserOptions([]);
      }
    } catch (error) {
      setUserOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (values?.processIds?.length > 0) {
      getUserOption();
    } else {
      setUserOptions([]);
    }
  }, [values?.processIds]);

  const selectedOption = useMemo(
    () => archiveOptions.find((opt) => opt.value === values.archiveId),
    [values.archiveId, archiveOptions]
  );

  const selectedLabel = selectedOption?.label || null;

  const { minDate, maxDate } = useMemo(() => {
    return getDateRangeFromLabel(selectedLabel, timezone);
  }, [selectedLabel, timezone]);

  useEffect(() => {
    if (minDate && maxDate) {
      setFieldValue('from_date', minDate);
      setFieldValue('to_date', maxDate);
    }
  }, [minDate?.getTime(), maxDate?.getTime()]);

  return (
    <div>
      <AdminHeader
        title="Audit Report"
        isSearch={true}
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
      />
      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="Month"
                  placeholder="Select month"
                  value={values.archiveId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="archiveId"
                  options={archiveOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormDateInput
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label="From Date"
                  showTimeSelect={true}
                  minDate={minDate}
                  maxDate={values.to_date || maxDate}
                  value={values.from_date}
                  onChange={(e) => setFieldValue('from_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormDateInput
                  label="To Date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  showTimeSelect={true}
                  minDate={values.from_date || minDate}
                  maxDate={maxDate}
                  value={values.to_date}
                  onChange={(e) => setFieldValue('to_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="Process"
                  placeholder="Select Process"
                  value={values.processIds}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="processIds"
                  options={processOptions}
                  touched={touched}
                  errors={errors}
                  isMulti={true} // Enable multi-select
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="User"
                  placeholder="Select User"
                  value={values.userIds}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="userIds"
                  options={userOptions}
                  touched={touched}
                  errors={errors}
                  isMulti={true} // Enable multi-select
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  name="auditorId"
                  label="Auditor"
                  placeholder="Select Auditor"
                  value={values.auditorId}
                  options={auditorOptions}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 328px)' : 'calc(100vh - 166px)' }}>
          <CallexLoader />
        </Card>
      ) : reportData?.data?.length > 0 ? (
        <React.Fragment>
          <div className="dashboard-card-container d-flex align-items-center gap-4">
            <Card className="report-card px-2 w-50" style={{ borderBottom: `2px solid #122335` }}>
              <div className="card-icon" style={{ backgroundColor: '#12233533' }}>
                <div className="icon mr-2 fs-5" style={{ color: '#122335' }}>
                  <Icon.Percent size={20} />
                </div>
              </div>
              <div className="d-flex flex-column ps-2">
                <span className="fs-6">Percentage</span>
                <span className="fs-4">{reportData?.grandTotal?.percentage}%</span>
              </div>
            </Card>
            <Card className="report-card px-2 w-50" style={{ borderBottom: `2px solid #2cd07e` }}>
              <div className="card-icon" style={{ backgroundColor: '#2cd07e33' }}>
                <div className="icon mr-2 fs-5" style={{ color: '#2cd07e' }}>
                  <Icon.PieChart size={20} />
                </div>
              </div>
              <div className="d-flex flex-column ps-2">
                <span className="fs-6">Total Count</span>
                <span className="fs-4">{reportData?.grandTotal?.totalCount}</span>
              </div>
            </Card>
          </div>
          <Card className="p-0 position-relative">
            <div
              className="daily-report-table"
              style={{ height: isFilterOpen ? 'calc(100vh - 465px)' : 'calc(100vh - 302px)' }}>
              <table className="report-list-table">
                <thead>
                  <tr>
                    {reportData?.header?.map((val, index) => {
                      return <th key={index}>{val.label}</th>;
                    })}
                  </tr>
                </thead>
                <tbody>
                  {reportData?.data?.map((val, index) => {
                    return (
                      <tr key={index}>
                        {reportData?.header?.map((value, idx) => {
                          return <td key={idx}>{val[value?.name] || '-'}</td>;
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
              <Pagination
                totalCount={paginationDetails?.total}
                limit={paginationDetails?.limit}
                page={paginationDetails?.page}
                onLimitChange={(val) => {
                  setFieldValue('limit', val);
                  setFieldValue('page', 1);
                }}
                onPageClick={(val) => setFieldValue('page', val)}
                lastPageClick={(val) => setFieldValue('page', val)}
                firstPageClick={(val) => setFieldValue('page', val)}
                onRefreshClick={() => onSubmit()}
              />
            </div>
          </Card>
        </React.Fragment>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 328px)' : 'calc(100vh - 166px)' }}>
          No data available
        </Card>
      )}
    </div>
  );
};

export default TenantAuditReport;
